import React, { useMemo, useCallback, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Switch,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import {
  Edit as EditIcon,
  Work as WorkIcon,
} from "@mui/icons-material";
import {
  updateSelectedAccount,
  updateCurrentAccountInDB,
  updateDisconnectAccountsforUnit,
  getMedFormHistoryForAccount,
  deleteForwardingAddress,
} from "modules/account/api/actions";
import { setSnackbar } from "modules/snackbar/api/snackbar";
import { useAccountToggle } from "../hooks/useAccountToggle";
import { hasActiveItemInRange, hasItemsInLastMonths } from "utils/Utils";
import { DIALOGS } from "../hooks/useDialogManager";
import Gate from "components/Gate";
import { store } from "../../../setup/store";
import {
  hasActivePaymentPlan,
  getPaymentPlanToggleMismatch,
  getPaymentPlanMismatchMessage,
} from "../utils/paymentPlanUtils";

const AccountControlsCard = React.memo(function AccountControlsCard({ openDialog }) {
  const dispatch = useDispatch();
  const account = useSelector((state) => state.account);
  const payments = useSelector((state) => state.payments);
  const toggleField = useAccountToggle();
  const [noDefaultAutoPayExplainOpen, setNoDefaultAutoPayExplainOpen] = useState(false);
  const [paymentPlanOffConfirmOpen, setPaymentPlanOffConfirmOpen] = useState(false);

  const paymentPlanItems = payments.paymentPlans?.items;
  const hasActivePlan = useMemo(
    () => hasActivePaymentPlan(paymentPlanItems),
    [paymentPlanItems]
  );
  const paymentPlanMismatch = useMemo(
    () => getPaymentPlanToggleMismatch(account.data.account?.paymentPlan, paymentPlanItems),
    [account.data.account?.paymentPlan, paymentPlanItems]
  );
  const paymentPlanMismatchMessage = getPaymentPlanMismatchMessage(paymentPlanMismatch);

  const hasActiveMedFormHistory = useMemo(
    () => hasActiveItemInRange(account.medFormHistory?.items),
    [account.medFormHistory?.items]
  );

  const hasThreeMedFormsInLastYear = useMemo(
    () => hasItemsInLastMonths(account.medFormHistory?.items, 12, 3),
    [account.medFormHistory?.items]
  );

  const handleDisconnectedToggle = useCallback(
    (event) => {
      const checked = event.target.checked;
      const updatedAccount = { ...account.data.account, disconnected: checked };
      const updatedData = { ...account.data, account: updatedAccount };
      dispatch(updateDisconnectAccountsforUnit(account.data.account.uuid, checked)).then(() => {
        dispatch(updateSelectedAccount(updatedData));
        dispatch(setSnackbar(true, "success", "Saving Successful"));
      });
    },
    [account.data, dispatch]
  );

  const handleInCollectionsToggle = useCallback(
    (event) => {
      const checked = event.target.checked;
      const updatedAccount = {
        ...account.data.account,
        inCollections: checked,
        inCollectionsDate: checked ? new Date() : null,
      };
      const updatedData = { ...account.data, account: updatedAccount };
      dispatch(updateSelectedAccount(updatedData));
      dispatch(setSnackbar(true, "warning", "Saving Account Detail Changes"));
      dispatch(updateCurrentAccountInDB(updatedAccount)).then(() => {
        dispatch(setSnackbar(true, "success", "Saving Successful"));
      });
    },
    [account.data, dispatch]
  );

  const handleWinterCrisisToggle = useCallback(
    (event) => {
      if (event.target.checked) {
        openDialog(DIALOGS.WINTER_CRISIS_PAYMENT);
      }
    },
    [openDialog]
  );

  const handleMedFormToggle = useCallback(
    (event) => {
      if (event.target.checked && !hasActiveMedFormHistory && !hasThreeMedFormsInLastYear) {
        const updatedAccount = { ...account.data.account, medForm: true };
        const updatedData = { ...account.data, account: updatedAccount };
        dispatch(updateSelectedAccount(updatedData));
        dispatch(setSnackbar(true, "warning", "Saving Account Detail Changes"));

        Promise.resolve(dispatch(updateCurrentAccountInDB(updatedAccount)))
          .then(() => {
            if (!updatedAccount.accountNum) {
              dispatch(setSnackbar(true, "error", "Failed to update Med Form: missing account number"));
              return;
            }
            return dispatch(getMedFormHistoryForAccount(updatedAccount.accountNum));
          })
          .then(() => {
            const items = store.getState().account.medFormHistory?.items || [];
            if (hasActiveItemInRange(items)) {
              dispatch(setSnackbar(true, "success", "Med Form enabled. History entry created."));
              return;
            }
            dispatch(setSnackbar(true, "error", "Failed to create Med Form history entry"));
            dispatch(updateSelectedAccount({
              ...account.data,
              account: { ...updatedAccount, medForm: false },
            }));
          })
          .catch((error) => {
            console.error("Error updating medForm:", error);
            dispatch(setSnackbar(true, "error", "Failed to update Med Form"));
            dispatch(updateSelectedAccount({
              ...account.data,
              account: { ...updatedAccount, medForm: false },
            }));
          });
      }
    },
    [account.data, hasActiveMedFormHistory, hasThreeMedFormsInLastYear, dispatch]
  );

  const persistPaymentPlanFlag = useCallback(
    (paymentPlan) => {
      const updatedAccount = { ...account.data.account, paymentPlan };
      const updatedData = { ...account.data, account: updatedAccount };
      dispatch(updateSelectedAccount(updatedData));
      dispatch(setSnackbar(true, "warning", "Saving Account Detail Changes"));
      return dispatch(
        updateCurrentAccountInDB({
          uuid: account.data.account.uuid,
          paymentPlan,
        })
      ).then(() => {
        dispatch(setSnackbar(true, "success", "Payment Plan status updated"));
      });
    },
    [account.data, dispatch]
  );

  const handlePaymentPlanToggle = useCallback(
    (event) => {
      if (event.target.checked) {
        if (hasActivePlan) {
          persistPaymentPlanFlag(true);
          return;
        }
        dispatch(
          setSnackbar(
            true,
            "info",
            "No Active payment plan on this account. Create or activate one to keep the flag consistent."
          )
        );
        openDialog(DIALOGS.PAYMENT_PLAN);
        return;
      }

      if (hasActivePlan) {
        setPaymentPlanOffConfirmOpen(true);
        return;
      }

      persistPaymentPlanFlag(false);
    },
    [dispatch, hasActivePlan, openDialog, persistPaymentPlanFlag]
  );

  const handlePaymentPlanOffConfirmClose = useCallback(() => {
    setPaymentPlanOffConfirmOpen(false);
  }, []);

  const handlePaymentPlanOffConfirm = useCallback(() => {
    setPaymentPlanOffConfirmOpen(false);
    persistPaymentPlanFlag(false);
  }, [persistPaymentPlanFlag]);

  const handleAutoPayToggle = useCallback(
    (event) => {
      if (event.target.checked) {
        const items = payments.accountAutoPayments?.items || [];
        const hasRunnableDefault = items.some(
          (ap) => ap.isDefault === 1 || ap.isDefault === true
        );
        if (hasRunnableDefault) {
          const updatedAccount = { ...account.data.account, autoPayments: 1 };
          const updatedData = { ...account.data, account: updatedAccount };
          dispatch(updateSelectedAccount(updatedData));
          dispatch(setSnackbar(true, "warning", "Saving Account Detail Changes"));
          dispatch(updateCurrentAccountInDB(updatedAccount)).then(() => {
            dispatch(setSnackbar(true, "success", "Saving Successful"));
          });
        } else {
          setNoDefaultAutoPayExplainOpen(true);
        }
      } else {
        toggleField("autoPayments", false);
      }
    },
    [account.data, dispatch, payments.accountAutoPayments?.items, toggleField]
  );

  const handleNoDefaultExplainClose = useCallback(() => {
    setNoDefaultAutoPayExplainOpen(false);
  }, []);

  const handleNoDefaultOpenAutoPay = useCallback(() => {
    setNoDefaultAutoPayExplainOpen(false);
    openDialog(DIALOGS.AUTO_PAY);
  }, [openDialog]);

  const handleForwardingAddressToggle = useCallback(
    async (event) => {
      if (event.target.checked) {
        const newForwardingAddress = account.newForwardingAddress
          ? { ...account.newForwardingAddress }
          : {};
        const updatedData = {
          ...account.data,
          forwardingAddress: [...(account.data.forwardingAddress || []), newForwardingAddress],
        };
        dispatch(updateSelectedAccount(updatedData));
        openDialog(DIALOGS.FORWARDING_ADDRESS);
      } else {
        if (account.data.forwardingAddress && account.data.forwardingAddress.length > 0) {
          const addressToDelete = account.data.forwardingAddress[0];
          const updatedData = { ...account.data, forwardingAddress: [] };
          dispatch(updateSelectedAccount(updatedData));

          if (addressToDelete.id) {
            try {
              await dispatch(deleteForwardingAddress(addressToDelete));
            } catch (error) {
              console.error("Error deleting forwarding address:", error);
              dispatch(setSnackbar(true, "error", "Error deleting forwarding address"));
              return;
            }
          }
        }
        dispatch(setSnackbar(true, "warning", "Saving Account Detail Changes"));
        dispatch(updateCurrentAccountInDB(account.data.account)).then(() => {
          dispatch(setSnackbar(true, "success", "Saving Successful"));
        });
      }
    },
    [account.data, account.newForwardingAddress, dispatch, openDialog]
  );

  const acct = account.data.account;
  const labelSx = { fontSize: "0.7rem", fontWeight: 500 };

  if (account.isLoading) {
    return (
      <Card variant="outlined" sx={{ p: 3, flex: 1, display: "flex", flexDirection: "column" }}>
        <Typography variant="h6" fontWeight="600" sx={{ mb: 3, display: "flex", alignItems: "center", gap: 1 }}>
          <WorkIcon color="info" fontSize="small" />
          Account Controls
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Box key={i} sx={{ flex: { xs: "1 1 calc(50% - 8px)", sm: "1 1 calc(33% - 12px)", md: "1 1 calc(16% - 12px)" } }}>
              <Skeleton variant="rectangular" height={32} sx={{ borderRadius: 1 }} />
            </Box>
          ))}
        </Box>
      </Card>
    );
  }

  return (
    <Card variant="outlined" sx={{ p: 3, flex: 1, display: "flex", flexDirection: "column" }}>
      <Typography variant="h6" fontWeight="600" sx={{ mb: 3, display: "flex", alignItems: "center", gap: 1 }}>
        <WorkIcon color="info" fontSize="small" />
        Account Controls
      </Typography>

      <Box spacing={2} display="flex" justifyContent="space-between">
        {/* Left column */}
        <Box display="flex" flexDirection="column">
          <ControlRow label="Printed Bills">
            <Gate capability="bills"><Switch size="small" name="printedBills" checked={Boolean(acct?.printedBills)} onChange={(e) => toggleField("printedBills", e.target.checked)} /></Gate>
          </ControlRow>
          <ControlRow label="E-Bills">
            <Gate capability="bills"><Switch size="small" name="eBills" checked={Boolean(acct?.eBills)} onChange={(e) => toggleField("eBills", e.target.checked)} /></Gate>
          </ControlRow>
          <ControlRow label="Disconnected">
            <Gate capability="disconnects"><Switch size="small" name="disconnected" checked={Boolean(acct?.disconnected)} onChange={handleDisconnectedToggle} /></Gate>
          </ControlRow>
          <ControlRow label="Stop Payment">
            <Gate capability="paymentSetup"><Switch size="small" name="stopPayment" checked={Boolean(acct?.stopPayment)} onChange={(e) => toggleField("stopPayment", e.target.checked)} /></Gate>
          </ControlRow>
          <ControlRow label="Bankruptcy">
            <Gate capability="paymentSetup"><Switch size="small" name="bankruptcy" checked={Boolean(acct?.bankruptcy)} onChange={(e) => toggleField("bankruptcy", e.target.checked)} /></Gate>
          </ControlRow>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="body2" color="text.secondary" sx={labelSx}>In Collections</Typography>
              {(acct?.inCollectionsDate != null || acct?.inCollections == 1) && (
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.65rem" }}>
                  {acct?.inCollectionsDate
                    ? new Date(acct.inCollectionsDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
                    : new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                </Typography>
              )}
            </Box>
            <Gate capability="paymentSetup"><Switch size="small" name="inCollections" checked={Boolean(acct?.inCollections)} onChange={handleInCollectionsToggle} /></Gate>
          </Box>
        </Box>

        {/* Right column */}
        <Box display="flex" flexDirection="column">
          {/* Winter Crisis */}
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap={1} flex={1}>
              <Typography variant="body2" color="text.secondary" sx={labelSx}>Winter Crisis</Typography>
              {!account.isLoading && Boolean(acct?.winterCrisis) && (
                <Gate capability="paymentSetup"><IconButton size="small" onClick={() => openDialog(DIALOGS.WINTER_CRISIS_HISTORY)} sx={{ p: 0.5 }}>
                  <EditIcon fontSize="small" />
                </IconButton></Gate>
              )}
              {Boolean(account.winterCrisisAvailability?.isLoading) && (
                <Typography variant="caption" color="textSecondary" sx={{ fontSize: "0.65rem" }}>Checking...</Typography>
              )}
              {!account.winterCrisisAvailability?.isLoading && !account.winterCrisisAvailability?.available && (
                <Typography variant="caption" color="error" sx={{ fontSize: "0.65rem" }}>
                  {account.winterCrisisAvailability?.isActive ? "Already active" : "Not available"}
                </Typography>
              )}
            </Box>
            <Gate capability="paymentSetup"><Switch
              size="small"
              name="winterCrisis"
              checked={Boolean(acct?.winterCrisis)}
              disabled={Boolean(acct?.winterCrisis) || !account.winterCrisisAvailability?.available || account.winterCrisisAvailability?.isActive}
              onChange={handleWinterCrisisToggle}
            /></Gate>
          </Box>

          {/* Med Form */}
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="body2" color="text.secondary" sx={labelSx}>Med Form</Typography>
              {!account.isLoading && account.medFormHistory?.items?.length > 0 && (
                <Gate capability="disconnects"><IconButton size="small" onClick={() => openDialog(DIALOGS.MED_FORM_HISTORY)} sx={{ p: 0.5 }}>
                  <EditIcon fontSize="small" />
                </IconButton></Gate>
              )}
            </Box>
            <Gate capability="disconnects"><Switch
              size="small"
              name="medForm"
              checked={hasActiveMedFormHistory}
              disabled={hasActiveMedFormHistory || hasThreeMedFormsInLastYear}
              onChange={handleMedFormToggle}
            /></Gate>
          </Box>

          {/* Payment Plan */}
          <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="body2" color="text.secondary" sx={labelSx}>Payment Plan</Typography>
                {!payments.isLoading && paymentPlanItems?.length > 0 && (
                  <Gate capability="paymentSetup"><IconButton size="small" onClick={() => openDialog(DIALOGS.PAYMENT_PLAN)} sx={{ p: 0.5 }}>
                    <EditIcon fontSize="small" />
                  </IconButton></Gate>
                )}
              </Box>
              <Gate capability="paymentSetup"><Switch
                size="small"
                name="paymentPlan"
                checked={Boolean(acct?.paymentPlan)}
                onChange={handlePaymentPlanToggle}
              /></Gate>
            </Box>
          </Box>

          {/* Pledge Account */}
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="body2" color="text.secondary" sx={labelSx}>Pledge Account</Typography>
              {acct?.pledgeAccount == 1 && (
                <>
                  <Gate capability="paymentSetup"><IconButton size="small" onClick={() => openDialog(DIALOGS.PLEDGE_ACCOUNT)} sx={{ p: 0.5 }}>
                    <EditIcon fontSize="small" />
                  </IconButton></Gate>
                  {acct?.pledgeSource && (
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.65rem" }}>
                      {acct.pledgeSource}
                    </Typography>
                  )}
                </>
              )}
            </Box>
            <Gate capability="paymentSetup"><Switch
              size="small"
              name="pledgeAccount"
              checked={Boolean(acct?.pledgeAccount)}
              onChange={() => openDialog(DIALOGS.PLEDGE_ACCOUNT)}
            /></Gate>
          </Box>

          {/* Auto-Payments */}
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="body2" color="text.secondary" sx={labelSx}>Auto-Payments</Typography>
              {acct?.autoPayments == 1 && (
                <Gate capability="paymentSetup"><IconButton size="small" onClick={() => openDialog(DIALOGS.AUTO_PAY)} sx={{ p: 0.5 }}>
                  <EditIcon fontSize="small" />
                </IconButton></Gate>
              )}
            </Box>
            <Gate capability="paymentSetup"><Switch size="small" name="autoPayments" checked={Boolean(acct?.autoPayments)} onChange={handleAutoPayToggle} /></Gate>
          </Box>

          {/* Forwarding Address */}
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="body2" color="text.secondary" sx={labelSx}>Forwarding Address</Typography>
              {account.data.forwardingAddress && account.data.forwardingAddress.length > 0 && (
                <Gate capability="editRecords"><IconButton size="small" onClick={() => openDialog(DIALOGS.FORWARDING_ADDRESS)} sx={{ p: 0.5 }}>
                  <EditIcon fontSize="small" />
                </IconButton></Gate>
              )}
            </Box>
            <Gate capability="editRecords"><Switch
              size="small"
              name="forwardingAddress"
              checked={!!(account.data.forwardingAddress && account.data.forwardingAddress.length > 0)}
              onChange={handleForwardingAddressToggle}
            /></Gate>
          </Box>
        </Box>
      </Box>

      <Dialog
        open={paymentPlanOffConfirmOpen}
        onClose={handlePaymentPlanOffConfirmClose}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Turn off Payment Plan flag?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Turning off clears the account Payment Plan flag only. It does not cancel the Active plan. Cancel the plan in Payment Plan details if needed. Continue?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handlePaymentPlanOffConfirmClose} color="inherit">
            Cancel
          </Button>
          <Button onClick={handlePaymentPlanOffConfirm} variant="contained" color="warning">
            Turn off flag
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={noDefaultAutoPayExplainOpen}
        onClose={handleNoDefaultExplainClose}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Cannot turn on auto payments yet</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            There is no default payment method on file. Click Open Auto Pay to add a new default payment method to the account in order to turn on the Auto-Payments switch. Make sure to mark one as the default, then you can turn this switch on.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleNoDefaultExplainClose} color="inherit">
            Close
          </Button>
          <Button onClick={handleNoDefaultOpenAutoPay} variant="contained" color="primary">
            Open Auto Pay
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
});

function ControlRow({ label, children }) {
  return (
    <Box display="flex" justifyContent="space-between" alignItems="center">
      <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.7rem", fontWeight: 500 }}>
        {label}
      </Typography>
      {children}
    </Box>
  );
}

export default AccountControlsCard;
